import React, { useState, useRef, useEffect } from 'react';

const FlagFootballPlayDesigner = () => {
  // Definiciones de trayectorias
  const routeDefinitions = [
    { id: 1, name: "RECTO", color: "red" },
    { id: 2, name: "SLANT", color: "blue" },
    { id: 3, name: "ESC. AFUERA", color: "green" },
    { id: 4, name: "ESC. DENTRO", color: "green" },
    { id: 5, name: "COMEBACK", color: "purple" },
    { id: 6, name: "CURL", color: "purple" },
    { id: 7, name: "SILLA", color: "red" },
    { id: 8, name: "POSTE", color: "black" },
    { id: 9, name: "BANDERA", color: "black" }
  ];

  // Definir tamaño del campo
  const fieldWidth = 800;
  const fieldHeight = 500;
  const centerX = fieldWidth / 2;
  const lineOfScrimmage = fieldHeight * 0.6; // Línea de scrimmage al 60% de la altura

  // Estado para los jugadores - posicionados en la línea de scrimmage
  const [players, setPlayers] = useState([
    { id: 1, label: 'QB', x: centerX, y: lineOfScrimmage + 50, path: [], color: '#e53e3e', routeType: null, routeLength: 'corta' },
    { id: 2, label: 'C', x: centerX, y: lineOfScrimmage, path: [], color: '#dd6b20', routeType: null, routeLength: 'corta' },
    { id: 3, label: 'X', x: centerX - 150, y: lineOfScrimmage, path: [], color: '#38a169', routeType: null, routeLength: 'corta' },
    { id: 4, label: 'Y', x: centerX + 100, y: lineOfScrimmage, path: [], color: '#3182ce', routeType: null, routeLength: 'corta' },
    { id: 5, label: 'Z', x: centerX - 75, y: lineOfScrimmage, path: [], color: '#805ad5', routeType: null, routeLength: 'corta' },
  ]);

  // Estado para el jugador seleccionado
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  // Estado para el modo de dibujo
  const [drawMode, setDrawMode] = useState(false);
  // Estado para la trayectoria temporal (durante el dibujo)
  const [tempPath, setTempPath] = useState([]);
  // Estado para el nombre de la jugada
  const [playName, setPlayName] = useState('Nueva Jugada');
  // Estado para la posición del balón
  const [ballPosition, setBallPosition] = useState({ playerId: 1, visible: true });
  // Estado para la distancia de ruta seleccionada
  const [selectedRouteLength, setSelectedRouteLength] = useState('corta');
  
  const fieldRef = useRef(null);

  // Resetear la aplicación a la formación inicial
  const resetToInitialFormation = () => {
    setPlayers([
      { id: 1, label: 'QB', x: centerX, y: lineOfScrimmage + 50, path: [], color: '#e53e3e', routeType: null, routeLength: 'corta' },
      { id: 2, label: 'Centro', x: centerX, y: lineOfScrimmage, path: [], color: '#dd6b20', routeType: null, routeLength: 'corta' },
      { id: 3, label: 'X', x: centerX - 150, y: lineOfScrimmage, path: [], color: '#38a169', routeType: null, routeLength: 'corta' },
      { id: 4, label: 'Y', x: centerX + 100, y: lineOfScrimmage, path: [], color: '#3182ce', routeType: null, routeLength: 'corta' },
      { id: 5, label: 'Z', x: centerX - 75, y: lineOfScrimmage, path: [], color: '#805ad5', routeType: null, routeLength: 'corta' },
    ]);
    setBallPosition({ playerId: 1, visible: true });
    setPlayName('Nueva Jugada');
    setSelectedPlayer(null);
    setSelectedRouteLength('corta');
    setTempPath([]);
  };

  // Manejar el inicio del arrastre
  const handleDragStart = (e, id) => {
    if (drawMode) return;
    setSelectedPlayer(id);
  };

  // Manejar el movimiento durante el arrastre
  const handleMouseMove = (e) => {
    if (selectedPlayer === null) return;
    
    const fieldRect = fieldRef.current.getBoundingClientRect();
    const x = e.clientX - fieldRect.left;
    const y = e.clientY - fieldRect.top;
    
    if (drawMode) {
      // Actualizar la trayectoria temporal (no se guarda todavía)
      setTempPath([...tempPath, { x, y }]);
    } else {
      // Mover jugador
      setPlayers(players.map(player => 
        player.id === selectedPlayer 
          ? { ...player, x, y } 
          : player
      ));
    }
  };

  // Manejar el fin del arrastre
  const handleMouseUp = () => {
    if (drawMode) return;
    // No reseteamos el jugador seleccionado para mantener la selección
  };

  // Manejar clic derecho para confirmar la trayectoria
  const handleRightClick = (e) => {
    e.preventDefault();
    
    if (drawMode && selectedPlayer !== null && tempPath.length > 0) {
      // Guardar la trayectoria temporal al jugador seleccionado
      setPlayers(players.map(player => 
        player.id === selectedPlayer 
          ? { ...player, path: tempPath, routeType: 'custom', routeLength: selectedRouteLength } 
          : player
      ));
      
      // Limpiar la trayectoria temporal
      setTempPath([]);
    }
  };

  // Seleccionar un jugador
  const handlePlayerClick = (id) => {
    // Si estamos en modo dibujo y seleccionamos otro jugador, borrar la trayectoria temporal
    if (drawMode && selectedPlayer !== id) {
      setTempPath([]);
    }
    
    // Seleccionar/deseleccionar jugador
    if (drawMode) {
      setSelectedPlayer(id);
    } else {
      setSelectedPlayer(id === selectedPlayer ? null : id);
    }
  };

  // Cambiar la distancia de la ruta
  const changeRouteLength = (length) => {
    setSelectedRouteLength(length);
    
    // Si hay un jugador seleccionado con una ruta predefinida, actualizar la distancia
    if (selectedPlayer) {
      const player = players.find(p => p.id === selectedPlayer);
      if (player && player.routeType && player.routeType !== 'custom') {
        // Recalcular la ruta con la nueva distancia
        applyRoute(player.routeType, length);
      }
    }
  };

  // Aplicar ruta predefinida a un jugador
  const applyRoute = (routeType, length = selectedRouteLength) => {
    if (!selectedPlayer) return;
    
    const player = players.find(p => p.id === selectedPlayer);
    if (!player) return;
    
    const routeLength = length;
    const startX = player.x;
    const startY = player.y;
    let path = [];
    
    // Factor de escala basado en la longitud seleccionada (en yardas)
    let distance;
    if (routeLength === 'corta') distance = 5;
    else if (routeLength === 'media') distance = 10;
    else distance = 15;
    
    // La escala en píxeles - cada 20px es una yarda
    const yardScale = 20;
    const baseDistance = distance * yardScale;
    
    // Genera la ruta según el tipo
    switch (routeType) {
      case 1: // RECTO
        path = [
          { x: startX, y: startY - baseDistance }
        ];
        break;
      case 2: // SLANT
        path = [
          { x: startX + baseDistance, y: startY - baseDistance }
        ];
        break;
      case 3: // ESC. AFUERA
        path = [
          { x: startX, y: startY - baseDistance/2 },
          { x: startX + baseDistance, y: startY - baseDistance/2 }
        ];
        break;
      case 4: // ESC. DENTRO
        path = [
          { x: startX, y: startY - baseDistance/2 },
          { x: startX - baseDistance, y: startY - baseDistance/2 }
        ];
        break;
      case 5: // COMEBACK
        path = [
          { x: startX, y: startY - baseDistance },
          { x: startX - baseDistance/2, y: startY - baseDistance/2 }
        ];
        break;
      case 6: // CURL
        path = [
          { x: startX, y: startY - baseDistance/2 },
          { x: startX + baseDistance/2, y: startY - baseDistance/2 }
        ];
        break;
      case 7: // SILLA
        path = [
          { x: startX, y: startY - baseDistance/2 },
          { x: startX - baseDistance/2, y: startY - baseDistance/2 },
          { x: startX - baseDistance/2, y: startY - baseDistance }
        ];
        break;
      case 8: // POSTE
        path = [
          { x: startX, y: startY - baseDistance/2 },
          { x: startX + baseDistance/2, y: startY - baseDistance }
        ];
        break;
      case 9: // BANDERA
        path = [
          { x: startX, y: startY - baseDistance/2 },
          { x: startX - baseDistance/2, y: startY - baseDistance }
        ];
        break;
      default:
        break;
    }
    
    // Actualiza el jugador con la nueva ruta
    setPlayers(players.map(p => 
      p.id === selectedPlayer 
        ? { ...p, path, routeType, routeLength } 
        : p
    ));
    
    // Limpiar la trayectoria temporal
    setTempPath([]);
  };

  // Limpiar la ruta de un jugador
  const clearPath = (id) => {
    setPlayers(players.map(player => 
      player.id === id 
        ? { ...player, path: [], routeType: null } 
        : player
    ));
    if (id === selectedPlayer) {
      setTempPath([]);
    }
  };

  // Limpiar todas las rutas
  const clearAllPaths = () => {
    setPlayers(players.map(player => ({ ...player, path: [], routeType: null })));
    setTempPath([]);
  };

  // Agregar un nuevo jugador
  const addPlayer = () => {
    const newId = Math.max(...players.map(p => p.id)) + 1;
    const randomColor = '#' + Math.floor(Math.random()*16777215).toString(16);
    setPlayers([...players, { id: newId, label: 'X', x: centerX - 200, y: lineOfScrimmage, path: [], color: randomColor, routeType: null, routeLength: 'corta' }]);
  };

  // Eliminar un jugador
  const removePlayer = (id) => {
    // Si el jugador tiene el balón, asignar el balón a otro jugador
    if (ballPosition.playerId === id) {
      const otherPlayers = players.filter(p => p.id !== id);
      if (otherPlayers.length > 0) {
        setBallPosition({ ...ballPosition, playerId: otherPlayers[0].id });
      } else {
        setBallPosition({ ...ballPosition, visible: false });
      }
    }
    setPlayers(players.filter(player => player.id !== id));
    if (selectedPlayer === id) {
      setSelectedPlayer(null);
      setTempPath([]);
    }
  };

  // Cambiar etiqueta de un jugador
  const changeLabel = (id, label) => {
    setPlayers(players.map(player => 
      player.id === id 
        ? { ...player, label } 
        : player
    ));
  };

  // Cambiar color de un jugador
  const changeColor = (id, color) => {
    setPlayers(players.map(player => 
      player.id === id 
        ? { ...player, color } 
        : player
    ));
  };

  // Asignar el balón a un jugador
  const assignBall = (playerId) => {
    setBallPosition({ playerId, visible: true });
  };

  // Posiciones disponibles
  const positionOptions = ['QB', 'Centro', 'X', 'Y', 'Z'];

  // Guardar la imagen como PNG
  const saveAsImage = () => {
    const canvas = document.createElement('canvas');
    const field = fieldRef.current;
    const width = field.offsetWidth;
    const height = field.offsetHeight;
    
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    
    // Dibujar fondo blanco
    context.fillStyle = 'white';
    context.fillRect(0, 0, width, height);
    
    // Dibujar líneas verticales (líneas de campo)
    context.strokeStyle = '#cccccc';
    context.lineWidth = 1;
    
    for (let i = 1; i < 4; i++) {
      context.beginPath();
      context.moveTo((width/4) * i, 0);
      context.lineTo((width/4) * i, height);
      context.stroke();
    }
    
    // Línea central gruesa (línea de scrimmage)
    context.beginPath();
    context.moveTo(0, lineOfScrimmage);
    context.lineTo(width, lineOfScrimmage);
    context.strokeStyle = '#333333';
    context.lineWidth = 3;
    context.stroke();
    
    // Dibujar líneas horizontales (yardas)
    const yardHeight = (lineOfScrimmage) / 15; // Altura de cada yarda desde la línea de scrimmage
    
    for (let i = 0; i <= 15; i++) {
      const y = lineOfScrimmage - ((i) * yardHeight);
      if (y < 0) continue; // No dibujar fuera del campo
      
      context.beginPath();
      context.moveTo(0, y);
      context.lineTo(width, y);
      context.strokeStyle = i % 5 === 0 ? '#666666' : '#eeeeee';
      context.lineWidth = i % 5 === 0 ? 1.5 : 0.5;
      context.stroke();
      
      // Añadir números de yardas cada 5 yardas
      if (i % 5 === 0 && i > 0) {
        context.fillStyle = "#333333";
        context.font = "12px Arial";
        context.fillText(`${i}`, 5, y - 2);
      }
      }
      // Asignar el balón a un jugador
const assignBall = (playerId) => {
    const player = players.find(p => p.id === playerId);
    
    // Verificar si el jugador tiene una trayectoria
    if (player && player.path.length > 0) {
      setBallPosition({ playerId, visible: true });
    } else {
      // Mostrar mensaje de error
      alert("Este jugador no tiene una trayectoria asignada. Asigna una trayectoria primero.");
    }
  };
    
    // Dibujar trayectorias
    players.forEach(player => {
      if (player.path.length > 0) {
        context.beginPath();
        context.moveTo(player.x, player.y);
        player.path.forEach(point => {
          context.lineTo(point.x, point.y);
        });
        context.strokeStyle = player.color;
        context.lineWidth = 2;
        context.setLineDash([]);
        context.stroke();
        
        // Punto final de la trayectoria
        context.beginPath();
        context.arc(player.path[player.path.length - 1].x, player.path[player.path.length - 1].y, 3, 0, 2 * Math.PI);
        context.fillStyle = player.color;
        context.fill();
      }
    });
    
    // Dibujar jugadores
    players.forEach(player => {
      // Círculo del jugador
      context.beginPath();
      context.arc(player.x, player.y, 20, 0, 2 * Math.PI);
      context.fillStyle = player.color;
      context.fill();
      
      // Texto del jugador
      context.fillStyle = 'white';
      context.font = 'bold 12px Arial';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(player.label, player.x, player.y);
      
      // Dibujar balón si corresponde
      if (ballPosition.visible && ballPosition.playerId === player.id) {
        context.beginPath();
        context.ellipse(player.x, player.y - 25, 8, 5, 0, 0, 2 * Math.PI);
        context.fillStyle = 'brown';
        context.fill();
        context.strokeStyle = 'black';
        context.lineWidth = 1;
        context.stroke();
      }
    });
    
    // Agregar título de la jugada
    context.fillStyle = 'black';
    context.font = 'bold 16px Arial';
    context.textAlign = 'center';
    context.fillText(playName, width/2, 20);
    
    // Descargar la imagen
    const link = document.createElement('a');
    link.download = `${playName.replace(/\s+/g, '_')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="flex flex-col w-full max-w-6xl mx-auto p-4">
      <div className="mb-4 flex justify-between items-center">
        <input
          type="text"
          value={playName}
          onChange={(e) => setPlayName(e.target.value)}
          className="px-2 py-1 border rounded-md"
          placeholder="Nombre de la jugada"
        />
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setDrawMode(!drawMode);
              setTempPath([]);
            }}
            className={`px-3 py-1 rounded-md ${drawMode ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            {drawMode ? 'Modo Dibujo: ON' : 'Modo Dibujo: OFF'}
          </button>
          <button
            onClick={clearAllPaths}
            className="px-3 py-1 bg-red-500 text-white rounded-md"
          >
            Limpiar Rutas
          </button>
          <button
            onClick={resetToInitialFormation}
            className="px-3 py-1 bg-yellow-500 text-white rounded-md"
          >
            Reiniciar
          </button>
          <button
            onClick={saveAsImage}
            className="px-3 py-1 bg-green-600 text-white rounded-md"
          >
            Guardar Imagen
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Campo de juego */}
        <div 
          ref={fieldRef}
          className="relative w-full h-96 bg-white border-2 border-gray-300 rounded-md overflow-hidden"
          style={{ width: `${fieldWidth}px`, height: `${fieldHeight}px` }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onContextMenu={handleRightClick}
        >
          {/* Líneas de campo verticales */}
          <div className="absolute left-0 top-0 w-full h-full">
            <div className="absolute left-1/4 top-0 w-0.5 h-full bg-gray-300"></div>
            <div className="absolute left-1/2 top-0 w-0.5 h-full bg-gray-300"></div>
            <div className="absolute left-3/4 top-0 w-0.5 h-full bg-gray-300"></div>
          </div>
          
          {/* Línea horizontal gruesa de scrimmage (0 yardas) */}
          <div 
            className="absolute left-0 w-full h-1 bg-gray-800"
            style={{ top: `${lineOfScrimmage}px` }}
          ></div>
          
          {/* Líneas de yardas (desde la línea de scrimmage hacia arriba) */}
          {Array.from({ length: 16 }).map((_, i) => {
            const yardY = lineOfScrimmage - ((i) * (lineOfScrimmage) / 15);
            return (
              <div 
                key={i} 
                className={`absolute left-0 w-full h-px ${i % 5 === 0 ? 'bg-gray-500' : 'bg-gray-200'}`}
                style={{ top: `${yardY}px` }}
              >
                {i > 0 && i % 5 === 0 && (
                  <div className="absolute left-1 text-xs text-gray-700 font-bold">{i}</div>
                )}
              </div>
            );
          })}

          {/* Trayectorias de los jugadores */}
          {players.map(player => (
            player.path.length > 0 && (
              <svg key={`path-${player.id}`} className="absolute top-0 left-0 w-full h-full pointer-events-none">
                <path
                  d={`M ${player.x} ${player.y} ${player.path.map(point => `L ${point.x} ${point.y}`).join(' ')}`}
                  stroke={player.color}
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="0"
                />
                <circle
                  cx={player.path[player.path.length - 1]?.x}
                  cy={player.path[player.path.length - 1]?.y}
                  r="3"
                  fill={player.color}
                />
              </svg>
            )
          ))}

          {/* Trayectoria temporal (mientras se dibuja) - visible solo cuando se hace clic derecho */}
          {/* Nota: no mostramos esta trayectoria hasta que se confirme con clic derecho */}

          {/* Jugadores */}
          {players.map(player => (
            <div
              key={player.id}
              className="absolute w-10 h-10 rounded-full flex items-center justify-center cursor-move select-none text-white"
              style={{ 
                left: player.x - 20, 
                top: player.y - 20,
                backgroundColor: player.color,
                boxShadow: selectedPlayer === player.id ? '0 0 0 2px white, 0 0 0 4px black' : 'none'
              }}
              onMouseDown={(e) => handleDragStart(e, player.id)}
              onClick={() => handlePlayerClick(player.id)}
            >
              {player.label}
               
              {/* Balón */}
              {ballPosition.visible && ballPosition.playerId === player.id && (
                <div className="absolute -top-6 left-2 w-6 h-4 bg-yellow-800 rounded-full transform rotate-45 border border-black"></div>
              )}
            </div>
          ))}
        </div>

        {/* Panel lateral con árbol de trayectorias (solo visible cuando se selecciona un jugador Y el modo dibujo está activado) */}
        {selectedPlayer && drawMode && (
          <div className="w-64 bg-gray-100 rounded-md border p-4">
            <h3 className="font-bold mb-2">Árbol de Trayectorias</h3>
            
            {/* Selector de distancia */}
            <div className="mb-4">
              <p className="text-sm font-medium mb-1">Distancia:</p>
              <div className="flex gap-2">
                <button
                  className={`px-2 py-1 text-xs rounded ${selectedRouteLength === 'corta' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => changeRouteLength('corta')}
                >
                  Corta (5 yardas)
                </button>
                <button
                  className={`px-2 py-1 text-xs rounded ${selectedRouteLength === 'media' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => changeRouteLength('media')}
                >
                  Media (10 yardas)
                </button>
                <button
                  className={`px-2 py-1 text-xs rounded ${selectedRouteLength === 'larga' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                  onClick={() => changeRouteLength('larga')}
                >
                  Larga (15 yardas)
                </button>
              </div>
            </div>
            
            {/* Lista de trayectorias */}
            <div className="flex flex-col gap-2">
              {routeDefinitions.map(route => (
                <button
                  key={route.id}
                  className="px-2 py-1 text-sm rounded hover:bg-gray-200 flex items-center border-l-4"
                  style={{ borderLeftColor: route.color }}
                  onClick={() => applyRoute(route.id)}
                >
                  <span className="mr-2">{route.id}.</span> {route.name}
                </button>
              ))}
            </div>
            
            <div className="mt-4 text-xs text-gray-600">
              <p><strong>Nota:</strong> Para dibujar manualmente:</p>
              <ol className="pl-4 mt-1 list-decimal">
                <li>Mueva el cursor para trazar</li>
                <li>Haga clic derecho para confirmar</li>
              </ol>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4">
        <h3 className="font-bold mb-2">Jugadores</h3>
        <div className="space-y-2">
          {players.map(player => (
            <div key={player.id} className="flex items-center space-x-2">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs"
                style={{ backgroundColor: player.color }}
              >
                {player.label}
              </div>
              <select
                value={player.label}
                onChange={(e) => changeLabel(player.id, e.target.value)}
                className="border rounded px-2 py-1"
              >
                {positionOptions.map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
              <input
                type="color"
                value={player.color}
                onChange={(e) => changeColor(player.id, e.target.value)}
                className="w-8 h-8 border"
                title="Cambiar color"
              />
              <button
                onClick={() => assignBall(player.id)}
                className={`px-2 py-1 rounded text-sm ${ballPosition.playerId === player.id ? 'bg-yellow-500 text-white' : 'bg-gray-200'}`}
                title="Asignar balón a este jugador"
              >
                Balón
              </button>
              <button
                onClick={() => clearPath(player.id)}
                className="px-2 py-1 bg-gray-200 rounded text-sm"
              >
                Limpiar
              </button>
              <button
                onClick={() => removePlayer(player.id)}
                className="px-2 py-1 bg-red-100 rounded text-sm"
              >
                Eliminar
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addPlayer}
          className="mt-2 px-3 py-1 bg-green-500 text-white rounded-md"
        >
          + Nuevo Jugador
        </button>
      </div>

      <div className="mt-4 text-sm">
        <p><strong>Instrucciones:</strong></p>
        <ul className="list-disc pl-5">
          <li>Arrastrar jugadores para posicionarlos (cuando el Modo Dibujo está OFF)</li>
          <li>Activar "Modo Dibujo" para trazar rutas</li>
          <li>Seleccionar un jugador en Modo Dibujo para ver el árbol de trayectorias</li>
          <li>Elegir la distancia antes de seleccionar una trayectoria</li>
          <li>Para dibujar manualmente, mueva el cursor y haga clic derecho para confirmar</li>
          <li>Para modificar la distancia de una ruta existente, seleccione la nueva distancia</li>
          <li>Guardar la imagen de la jugada con el botón "Guardar Imagen"</li>
        </ul>
      </div>
    </div>
  );
};

export default FlagFootballPlayDesigner;